import type {
  BhgCallRecord,
  BhgScheduleSummary,
  BhgCallEvent,
  BhgSelfReportedScreen,
} from "./bhg-call-dashboard-api";

export const NOTIFICATION_CHANNEL_OPTIONS = ["sms", "email"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNEL_OPTIONS)[number];

export type BhgCallDashboardProvider = {
  fetchCalls: () => Promise<{
    calls: BhgCallRecord[];
    scheduleSummary: BhgScheduleSummary;
    /** Audio retention window for this user. Omitted means the standard window. */
    retentionDays?: number;
    updatedAt: string;
  }>;
  subscribeCalls: (
    onUpdate: (
      calls: BhgCallRecord[],
      scheduleSummary: BhgScheduleSummary,
      updatedAt: string
    ) => void,
    onError: () => void
  ) => () => void;
  fetchRecordingAvailability: (
    callSid: string
  ) => Promise<{ available: boolean; note: string | null }>;
  buildRecordingDownloadUrl: (callSid: string) => string;
  fetchCallEvents: (callSid: string) => Promise<{ events: BhgCallEvent[]; updatedAt: string }>;
  fetchClinics: () => Promise<BhgClinicOption[]>;
  fetchSubscription: () => Promise<BhgSubscriptionInfo | null>;
  fetchUserProfile: () => Promise<PhoneEmail | null>;
  fetchNotificationChannelAvailability?: () => Promise<{
    enabledChannels: NotificationChannel[];
  }>;
  updateClinic: (clinicId: number | null) => Promise<boolean>;
  updateNotifications: (input: {
    mobilePhone: string;
    notificationEmail: string;
    notificationChannels: ("sms" | "email")[];
  }) => Promise<boolean>;
  updateNotificationSchedule: (input: {
    notificationTime: string;
    notificationTimezone: string;
  }) => Promise<boolean>;
  // Optional so existing embedders and test doubles keep compiling; the
  // self-report affordance hides when they're absent.
  fetchSelfReportedScreens?: () => Promise<BhgSelfReportedScreen[]>;
  recordSelfReportedScreen?: (input: {
    screenDate?: string;
    note?: string;
  }) => Promise<{ ok: true; screen: BhgSelfReportedScreen } | { ok: false; error: string }>;
  voidSelfReportedScreen?: (id: number) => Promise<boolean>;
};

export type PhoneEmail = {
  email: string;
  phoneNumber: string;
};

export type BhgClinicOption = {
  id: number;
  name: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  dosingHours: string | null;
  operatingHours: string | null;
};

export type BhgSubscriptionInfo = {
  products: string[];
  bhgEnrollment: {
    userId: number;
    clinicId: number | null;
    clinicName: string | null;
    notificationTime: string;
    notificationTimezone: string;
    mobilePhone: string;
    notificationEmail: string;
    notificationChannels: ("sms" | "email")[];
    clinicDosingHours: string | null;
    clinicOperatingHours: string | null;
  } | null;
};

export type HTMLInputEvent = React.ChangeEvent<HTMLInputElement>;
export type HTMLInputEventHandler = (event: HTMLInputEvent) => void;
